import { Card, Skeleton } from '@/shared/components';
import {
  type StickyColumnConfig,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table';
import { cn } from '@/shared/utils';
import { tv } from 'tailwind-variants';

const tableSkeletonStyles = tv({
  slots: {
    card: 'w-full p-0 gap-0 mb-0 overflow-hidden mb-[4.2rem]',
    row: 'border-none',
    headerSkeleton: '',
    stickyCellContainer: 'flex items-center gap-[1rem]',
    stickyCellAvatar: '',
    stickyCellTitle: '',
    stickyCellSubtitle: '',
    regularCellSkeleton: '',
  },
  variants: {
    variant: {
      default: {
        headerSkeleton: 'h-[2rem] w-[8rem]',
        stickyCellAvatar: 'size-[1.6rem]',
        stickyCellTitle: 'h-[2rem] w-[10rem]',
        stickyCellSubtitle: 'h-[1.6rem] w-[12rem]',
        regularCellSkeleton: 'h-[2rem] w-[10rem]',
      },
      'large-icons': {
        headerSkeleton: 'h-[1.62em] w-[8.5rem]',
        stickyCellAvatar: 'size-[4.4rem]',
        stickyCellTitle: 'h-[1.6rem] w-[6.2rem]',
        stickyCellSubtitle: 'h-[1.2rem] w-[5.3rem]',
        regularCellSkeleton: 'h-[2.4rem] w-[6.9rem] ms-auto',
      },
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

interface TableSkeletonProps {
  columns?: number;
  rows?: number;
  stickyColumn?: StickyColumnConfig;
  headerClassName?: string;
  cellClassName?: string;
  stickyCellContent?: React.ReactNode;
  variant?: 'default' | 'large-icons';
}

export const TableSkeleton = ({
  columns = 5,
  rows = 5,
  stickyColumn,
  headerClassName,
  cellClassName,
  stickyCellContent,
  variant = 'default',
}: TableSkeletonProps) => {
  const stickyIndex = stickyColumn?.index ?? 0;
  const styles = tableSkeletonStyles({ variant });

  return (
    <Card className={styles.card()} variant="no-effects">
      <Table
        stickyColumn={{
          index: stickyIndex,
        }}>
        <TableHeader>
          <TableRow className={styles.row()}>
            {Array.from({ length: columns }).map((_, index) => (
              <TableHead
                key={index}
                stickyColumnIndex={
                  index === stickyIndex ? stickyIndex : undefined
                }
                className={headerClassName}>
                <Skeleton
                  className={cn(
                    styles.headerSkeleton(),
                    variant === 'large-icons' &&
                      index !== stickyIndex &&
                      'ms-auto'
                  )}
                />
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <TableRow
              key={rowIndex}
              rowIndex={rowIndex}
              className={styles.row()}>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <TableCell
                  key={colIndex}
                  rowIndex={rowIndex}
                  stickyColumnIndex={
                    colIndex === stickyIndex ? stickyIndex : undefined
                  }
                  className={cellClassName}>
                  {colIndex === stickyIndex ? (
                    stickyCellContent || (
                      <div className={styles.stickyCellContainer()}>
                        <Skeleton
                          variant="avatar"
                          className={styles.stickyCellAvatar()}
                        />
                        <div className="flex flex-col gap-[0.4rem]">
                          <Skeleton className={styles.stickyCellTitle()} />
                          <Skeleton className={styles.stickyCellSubtitle()} />
                        </div>
                      </div>
                    )
                  ) : (
                    <Skeleton className={styles.regularCellSkeleton()} />
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};
